// Settings overview del bot — cierra el dead-link `view_settings` del
// sidebar (gap m-3 audit). Vista de solo-lectura: muestra metadatos del
// bot y dirige al operador al editor para edits del DSL o al panel de
// credenciales para secretos. Edits in-place llegaran en una story futura.

import { Link, useParams } from '@tanstack/react-router';
import { ExternalLink, FileCode, KeyRound } from 'lucide-react';
import { MenuModeCard } from '@/components/bots/MenuModeCard';
import { TransferBotCard } from '@/components/bots/TransferBotCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MENU_MODE_LABELS } from '@/components/whatsapp-connection/MenuModeSelect';
import { useBot } from '@/hooks/queries/use-bots';

export function BotSettingsPage(): React.ReactElement {
  const params = useParams({ strict: false }) as { botId?: string };
  const botId = Number(params.botId);
  const { data, isLoading, isError } = useBot(botId, Number.isFinite(botId));

  if (isLoading) return <p className="text-sm text-muted-foreground">Cargando…</p>;
  if (isError || !data) {
    return <p className="text-sm text-destructive">No se pudo cargar la configuración del bot.</p>;
  }

  const config = (data.config ?? {}) as Record<string, unknown>;
  const botMeta = (config.bot ?? {}) as Record<string, unknown>;
  const wa = (config.whatsapp ?? {}) as Record<string, unknown>;
  const provider = typeof wa.provider === 'string' ? wa.provider : '—';
  const subBlock = (wa[provider] as Record<string, unknown> | undefined) ?? {};

  const rows: Array<{ label: string; value: React.ReactNode }> = [
    { label: 'Nombre', value: data.name },
    { label: 'Organización', value: data.organization?.name ?? '—' },
    { label: 'ID', value: <code className="font-mono text-xs">{data.id}</code> },
    { label: 'Estado', value: data.status },
    {
      label: 'Versión',
      value: typeof botMeta.version === 'string' ? botMeta.version : '—',
    },
    {
      label: 'Descripción',
      value: typeof botMeta.description === 'string' ? botMeta.description : '—',
    },
    {
      label: 'Idioma / Timezone',
      value: `${typeof botMeta.language === 'string' ? botMeta.language : '?'} / ${
        typeof botMeta.timezone === 'string' ? botMeta.timezone : '?'
      }`,
    },
    {
      label: 'Entry flow',
      value: (
        <code className="font-mono text-xs">
          {typeof botMeta.entry_flow === 'string' ? botMeta.entry_flow : '—'}
        </code>
      ),
    },
    { label: 'Provider', value: provider },
    {
      label: 'Phone Number ID',
      value:
        typeof subBlock.phoneNumberId === 'string' ? (
          <code className="font-mono text-xs">{subBlock.phoneNumberId}</code>
        ) : (
          '—'
        ),
    },
    {
      label: 'Instance',
      value:
        typeof subBlock.instanceName === 'string' ? (
          <code className="font-mono text-xs">{subBlock.instanceName}</code>
        ) : (
          '—'
        ),
    },
    ...(provider === 'evolution-go'
      ? [
          {
            label: 'Menús',
            value: MENU_MODE_LABELS[subBlock.interactive === 'native' ? 'native' : 'text'],
          },
        ]
      : []),
    {
      label: 'Created',
      value: <span className="text-xs">{new Date(data.createdAt).toLocaleString()}</span>,
    },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Settings — {data.name}</h1>
        <p className="text-sm text-muted-foreground">
          Vista de configuración del bot. Para editar el DSL del flujo, usa el editor; para
          credenciales (apiKey, accessToken), el panel del bot.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Configuración actual</CardTitle>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <tbody>
              {rows.map((r) => (
                <tr key={r.label} className="border-b border-border/50 last:border-0">
                  <td className="py-2 pr-3 text-muted-foreground">{r.label}</td>
                  <td className="py-2">{r.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Story 17-4 — modo de los menús (solo Evolution GO). */}
      {provider === 'evolution-go' && <MenuModeCard bot={data} />}

      <Card>
        <CardHeader>
          <CardTitle>Edición</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <Link
            to="/bots/$botId/editor"
            params={{ botId: String(data.id) }}
            className="flex items-center gap-2 rounded-md border border-border p-2 hover:bg-muted/50"
          >
            <FileCode className="h-4 w-4" aria-hidden />
            <span className="flex-1">
              <span className="font-medium">Editor</span>
              <span className="block text-xs text-muted-foreground">
                Modificar el DSL: flujos, voz, horarios.
              </span>
            </span>
            <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          </Link>
          <Link
            to="/bots/$botId"
            params={{ botId: String(data.id) }}
            className="flex items-center gap-2 rounded-md border border-border p-2 hover:bg-muted/50"
          >
            <KeyRound className="h-4 w-4" aria-hidden />
            <span className="flex-1">
              <span className="font-medium">Credenciales del provider</span>
              <span className="block text-xs text-muted-foreground">
                accessToken / apiKey — cifrados en BD.
              </span>
            </span>
            <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          </Link>
        </CardContent>
      </Card>

      <TransferBotCard bot={data} />
    </div>
  );
}
